import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lightning, ArrowRight, Check, Lock, QrCode, Users, 
  ChartLineUp, Wallet, Trophy, Star, Handshake, Globe,
  CurrencyDollar, Rocket, Crown, Shield, Target, Play,
  CaretLeft, X, Cross
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EcosystemToolsPage = () => {
  const navigate = useNavigate();
  const { user, isPaidMember, isAuthenticated } = useAuth();
  const [referrals, setReferrals] = useState([25]);
  const [conversionRate, setConversionRate] = useState([30]);
  const [showCalculator, setShowCalculator] = useState(false);

  // If not authenticated, redirect to login
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Calculate earnings
  const calculateEarnings = () => {
    const refs = referrals[0];
    const rate = conversionRate[0] / 100;
    const conversions = Math.floor(refs * rate);
    
    // EchoCard Pro is $9.99/month, 50% revenue share during events, 30% residual
    const eventCommission = conversions * 9.99 * 0.5;
    const monthlyResidual = conversions * 9.99 * 0.3;
    const yearlyResidual = monthlyResidual * 12;
    
    return {
      conversions,
      eventCommission: eventCommission.toFixed(2),
      monthlyResidual: monthlyResidual.toFixed(2),
      yearlyResidual: yearlyResidual.toFixed(2),
      totalFirstYear: (eventCommission + yearlyResidual).toFixed(2)
    };
  };

  const earnings = calculateEarnings();

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  // Locked overlay for free members
  if (!isPaidMember) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        {/* Back Navigation */}
        <header className="bg-[#121212] border-b border-[white/10] sticky top-0 z-50">
          <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <Link to="/community" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <CaretLeft size={20} weight="bold" />
              <span>Back to Community</span>
            </Link>
            <Link to={user ? "/community" : "/"} className="text-2xl font-black text-[#C4A661]">POWR</Link>
          </div>
        </header>

        {/* Locked Preview */}
        <div className="relative">
          {/* Blurred Background Preview */}
          <div className="filter blur-sm opacity-50 pointer-events-none">
            <section className="py-24 px-6 md:px-12">
              <div className="container mx-auto max-w-5xl text-center">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C4A661]/20 to-transparent border border-[#C4A661]/30 px-4 py-2 mb-8">
                  <Crown size={20} className="text-[#C4A661]" weight="fill" />
                  <span className="text-[#C4A661] font-mono text-sm uppercase tracking-wider">Inner Circle Exclusive</span>
                </div>
                <h1 className="text-4xl md:text-6xl font-black mb-6">
                  POWR x <span className="text-[#C4A661]">Identyx</span>
                </h1>
                <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                  Your first exclusive money-making opportunity as an Inner Circle member.
                </p>
              </div>
            </section>

            {/* Preview Cards */}
            <section className="py-12 px-6 md:px-12">
              <div className="container mx-auto max-w-5xl">
                <div className="grid md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-[#121212] border border-[white/10] p-8 h-48"></div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Lock Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/95 to-[#050505] flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center px-6 max-w-lg"
            >
              <div className="w-20 h-20 bg-[#121212] border border-[#C4A661]/30 rounded-full flex items-center justify-center mx-auto mb-8">
                <Lock size={40} className="text-[#C4A661]" weight="duotone" />
              </div>
              <h2 className="text-3xl md:text-4xl font-black mb-4">
                Inner Circle <span className="text-[#C4A661]">Exclusive</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                The Ecosystem Tools are available exclusively to Inner Circle members. 
                Upgrade now to unlock your first money-making opportunity with EchoCard.
              </p>
              <div className="space-y-4">
                <Link to="/inner-circle-sales">
                  <Button className="w-full h-14 bg-[#C4A661] hover:bg-[#D4B671] text-black font-bold text-lg rounded-none">
                    Unlock Inner Circle Access
                    <ArrowRight size={24} className="ml-2" weight="bold" />
                  </Button>
                </Link>
                <Link to="/community">
                  <Button variant="outline" className="w-full h-12 border-[white/20] text-gray-400 hover:text-white hover:border-[white/30] rounded-none">
                    Return to Community
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                Starting at $97/month or $497 lifetime
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Full access for paid members
  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[white/10] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/community" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <CaretLeft size={20} weight="bold" />
            <span className="hidden md:inline">Back to Community</span>
          </Link>
          <Link to={user ? "/community" : "/"} className="text-2xl font-black text-[#C4A661]">POWR</Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-gray-400">Inner Circle Member</span>
            <Crown size={20} className="text-[#C4A661]" weight="fill" />
          </div>
        </div>
      </header>

      {/* Scripture Banner */}
      <div className="bg-[#C4A661] py-3">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-[#0A0A0A] text-sm font-medium">
            <Cross size={14} className="inline-block mr-2 -mt-0.5" weight="bold" />
            "Commit your work to the Lord, and your plans will be established." — Proverbs 16:3
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 px-6 md:px-12 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#C4A661]/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C4A661] to-transparent" />
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            className="text-center"
          >
            <motion.div 
              variants={fadeUp}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C4A661]/20 to-transparent border border-[#C4A661]/30 px-4 py-2 mb-8"
            >
              <Crown size={20} className="text-[#C4A661]" weight="fill" />
              <span className="text-[#C4A661] font-mono text-sm uppercase tracking-wider">Inner Circle Exclusive</span>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-center justify-center gap-4 mb-6">
              <span className="text-4xl md:text-6xl font-black">POWR</span>
              <X size={32} className="text-[#C4A661]" weight="bold" />
              <span className="text-4xl md:text-6xl font-black text-[#C4A661]">Identyx</span>
            </motion.div>

            <motion.h1 
              variants={fadeUp}
              className="text-2xl md:text-4xl font-bold text-gray-300 mb-6"
            >
              Your First Money-Making Opportunity
            </motion.h1>

            <motion.p 
              variants={fadeUp}
              className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              As an exclusive POWR Inner Circle member, you have first-mover access to 
              <span className="text-white font-semibold"> EchoCard</span> — a revolutionary professional networking 
              platform. Earn up to <span className="text-[#C4A661] font-bold">50% revenue share</span> by 
              introducing others to smarter networking.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://identyx.net/signup?ref=powr" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="h-14 px-8 bg-[#C4A661] hover:bg-[#D4B671] text-black font-bold text-lg rounded-none w-full sm:w-auto">
                  Get Your EchoCard Now
                  <ArrowRight size={24} className="ml-2" weight="bold" />
                </Button>
              </a>
              <Button 
                variant="outline"
                onClick={() => setShowCalculator(true)}
                className="h-14 px-8 border-[#C4A661] text-[#C4A661] hover:bg-[#C4A661] hover:text-black font-bold text-lg rounded-none"
              >
                <CurrencyDollar size={24} className="mr-2" weight="bold" />
                Calculate Your Earnings
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What is EchoCard Section */}
      <section className="py-20 px-6 md:px-12 bg-[#121212]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C4A661] mb-4">
              The Opportunity
            </p>
            <h2 className="text-3xl md:text-5xl font-black mb-6">
              What is <span className="text-[#C4A661]">EchoCard</span>?
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              EchoCard is the future of professional networking — a digital business card platform 
              that turns every interaction into opportunity.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: QrCode,
                title: 'Instant QR Sharing',
                desc: 'One scan shares your entire professional identity. No apps needed for recipients.'
              },
              {
                icon: Globe,
                title: 'AI-Powered Profiles',
                desc: 'AI helps create stunning profiles that convert viewers into connections.'
              },
              {
                icon: ChartLineUp,
                title: 'LinkedIn Growth Engine',
                desc: 'Generate viral LinkedIn posts and connection scripts with one click.'
              },
              {
                icon: Target,
                title: 'Location Discovery',
                desc: 'Find professionals near you at events, conferences, or anywhere business happens.'
              },
              {
                icon: Users,
                title: 'Event Networking',
                desc: 'Dominate any conference with live presence broadcasting and instant connections.'
              },
              {
                icon: Wallet,
                title: 'Lead Capture',
                desc: 'Turn every handshake into a trackable, actionable lead with analytics.'
              }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="p-6 bg-[#050505] border-[white/10] rounded-none h-full hover:border-[#C4A661]/50 transition-all group">
                  <feature.icon size={36} className="text-[#C4A661] mb-4 group-hover:scale-110 transition-transform" weight="duotone" />
                  <h3 className="text-lg font-bold mb-2 text-white">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Revenue Opportunity Section */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#050505] to-[#121212]">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C4A661] mb-4">
              Your Income Potential
            </p>
            <h2 className="text-3xl md:text-5xl font-black mb-6">
              How You <span className="text-[#C4A661]">Make Money</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                percentage: '50%',
                title: 'Event Revenue Share',
                desc: 'Earn half of all EchoCard Pro subscriptions sold when you host or promote events.',
                color: 'from-[#C4A661] to-yellow-600'
              },
              {
                percentage: '30%',
                title: 'Residual Income',
                desc: 'Keep earning 30% on all renewals from people you referred — month after month.',
                color: 'from-purple-500 to-purple-700'
              },
              {
                percentage: '100%',
                title: 'First Mover Advantage',
                desc: 'As a POWR member, you get priority access before the general public.',
                color: 'from-emerald-500 to-emerald-700'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br opacity-10 group-hover:opacity-20 transition-opacity rounded-none" 
                     style={{ backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))` }} />
                <Card className="p-8 bg-[#121212] border-[white/10] rounded-none h-full text-center relative z-10">
                  <div className={`text-5xl md:text-6xl font-black bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-4`}>
                    {item.percentage}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button 
              onClick={() => setShowCalculator(true)}
              className="h-14 px-8 bg-[#C4A661] hover:bg-[#D4B671] text-black font-bold text-lg rounded-none"
            >
              <CurrencyDollar size={24} className="mr-2" weight="bold" />
              Calculate Your Potential Earnings
            </Button>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 md:px-12 bg-white text-black">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C4A661] mb-4">
              Simple Process
            </p>
            <h2 className="text-3xl md:text-5xl font-black mb-6">
              Get Started in <span className="text-[#C4A661]">4 Steps</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Create Your EchoCard',
                desc: 'Sign up through POWR\'s exclusive link and build your professional identity in 2 minutes.'
              },
              {
                step: '02',
                title: 'Share Everywhere',
                desc: 'Use your QR code, link, or NFC to share at events, on LinkedIn, and in every interaction.'
              },
              {
                step: '03',
                title: 'Invite Others',
                desc: 'Share EchoCard with your network. Every signup through your link is tracked.'
              },
              {
                step: '04',
                title: 'Earn Commissions',
                desc: 'Get paid when your referrals upgrade. Residual income builds every month.'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-6xl font-black text-[#C4A661]/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters for POWR Members */}
      <section className="py-20 px-6 md:px-12 bg-[#050505]">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#121212] to-[#050505] border border-[#C4A661]/30 p-8 md:p-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Handshake size={32} className="text-[#C4A661]" weight="duotone" />
              <h2 className="text-2xl md:text-3xl font-black">Why This Partnership Matters</h2>
            </div>
            
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p>
                POWR is about <span className="text-white font-semibold">building real wealth</span> — not just 
                talking about it. That's why we've partnered with Identyx to give Inner Circle members 
                <span className="text-[#C4A661] font-semibold"> first access</span> to a proven income opportunity.
              </p>
              <p>
                EchoCard solves a real problem: professionals are still networking with outdated paper cards 
                and losing connections. You're not selling hype — you're introducing people to a tool that 
                genuinely makes their professional lives better.
              </p>
              <p>
                This is the <span className="text-white font-semibold">first of many ecosystem tools</span> we'll 
                unlock for Inner Circle members. Your network is your net worth — and now you can monetize it.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-[white/10]">
              <div className="flex flex-wrap gap-6 justify-center text-center">
                {[
                  { value: '$9.99/mo', label: 'EchoCard Pro Price' },
                  { value: '50%', label: 'Your Event Share' },
                  { value: '30%', label: 'Residual Monthly' },
                  { value: 'Unlimited', label: 'Earning Potential' }
                ].map((stat, i) => (
                  <div key={i} className="min-w-[100px]">
                    <div className="text-2xl font-black text-[#C4A661]">{stat.value}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#121212] to-[#050505]">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Rocket size={48} className="text-[#C4A661] mx-auto mb-6" weight="duotone" />
            <h2 className="text-3xl md:text-4xl font-black mb-6">
              Ready to Start <span className="text-[#C4A661]">Earning</span>?
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-xl mx-auto">
              Get your EchoCard now and start building your network and income. 
              As a POWR member, you're already ahead of the curve.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://identyx.net/signup?ref=powr" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="h-14 px-8 bg-[#C4A661] hover:bg-[#D4B671] text-black font-bold text-lg rounded-none w-full sm:w-auto">
                  Create Your EchoCard
                  <ArrowRight size={24} className="ml-2" weight="bold" />
                </Button>
              </a>
              <a 
                href="https://identyx.net" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="h-14 px-8 border-[white/20] text-gray-300 hover:border-[#C4A661] hover:text-white rounded-none w-full sm:w-auto">
                  Learn More About Identyx
                  <Globe size={20} className="ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[white/10]">
        <div className="container mx-auto max-w-5xl flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xl font-black text-[#C4A661]">POWR</span>
            <X size={16} className="text-gray-500" />
            <span className="text-xl font-black text-gray-400">Identyx</span>
          </div>
          <p className="text-sm text-gray-500">
            Exclusive partnership for Inner Circle members
          </p>
          <Link to="/community" className="text-sm text-gray-400 hover:text-[#C4A661] transition-colors">
            Back to Community
          </Link>
        </div>
      </footer>

      {/* Earnings Calculator Modal */}
      <AnimatePresence>
        {showCalculator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCalculator(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#121212] border border-[white/10] w-full max-w-lg p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-black">Earnings Calculator</h3>
                <button 
                  onClick={() => setShowCalculator(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} weight="bold" />
                </button>
              </div>

              <div className="space-y-8">
                {/* Referrals Slider */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm text-gray-400">People You Refer</label>
                    <span className="text-2xl font-black text-[#C4A661]">{referrals[0]}</span>
                  </div>
                  <Slider
                    value={referrals}
                    onValueChange={setReferrals}
                    max={200}
                    min={5}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5</span>
                    <span>200</span>
                  </div>
                </div>

                {/* Conversion Rate Slider */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm text-gray-400">Conversion Rate</label>
                    <span className="text-2xl font-black text-[#C4A661]">{conversionRate[0]}%</span>
                  </div>
                  <Slider
                    value={conversionRate}
                    onValueChange={setConversionRate}
                    max={60}
                    min={10}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10%</span>
                    <span>60%</span>
                  </div>
                </div>

                {/* Results */}
                <div className="bg-[#050505] border border-[white/10] p-6">
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-400 mb-1">Estimated Conversions</p>
                    <p className="text-3xl font-black text-white">{earnings.conversions} people</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 bg-[#121212] border border-[white/10]">
                      <p className="text-xs text-gray-500 mb-1">Event Commission</p>
                      <p className="text-xl font-bold text-[#C4A661]">${earnings.eventCommission}</p>
                    </div>
                    <div className="text-center p-3 bg-[#121212] border border-[white/10]">
                      <p className="text-xs text-gray-500 mb-1">Monthly Residual</p>
                      <p className="text-xl font-bold text-purple-400">${earnings.monthlyResidual}</p>
                    </div>
                  </div>

                  <div className="text-center p-4 bg-gradient-to-r from-[#C4A661]/10 to-purple-500/10 border border-[#C4A661]/30">
                    <p className="text-sm text-gray-400 mb-1">Potential First Year Earnings</p>
                    <p className="text-4xl font-black text-[#C4A661]">${earnings.totalFirstYear}</p>
                    <p className="text-xs text-gray-500 mt-1">Event commission + 12 months residual</p>
                  </div>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  * Estimates based on EchoCard Pro at $9.99/mo. Actual results may vary based on your effort and network.
                </p>

                <a 
                  href="https://identyx.net/signup?ref=powr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full h-12 bg-[#C4A661] hover:bg-[#D4B671] text-black font-bold rounded-none">
                    Start Earning Now
                    <ArrowRight size={20} className="ml-2" weight="bold" />
                  </Button>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EcosystemToolsPage;
